/* eslint-disable */
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Spin,
  Table,
  Tag,
  message
} from 'antd';
import { LoadingOutlined, ZoomOutOutlined, EyeOutlined, HistoryOutlined, CalendarOutlined, FileTextOutlined } from '@ant-design/icons';
import type { ColumnsType, SorterResult } from 'antd/es/table/interface';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import InstitutionUserService from '../../services/InstitutionUserService';

const antIcon = <LoadingOutlined style={{ fontSize: '24px', color: '#37762F' }} spin />;
const { RangePicker } = DatePicker;
const { Option } = Select;

interface AuditLog {
  audit_logs_id: string;
  action: string;
  table_name: string;
  record_id: string | null;
  user_id: string | null;
  created_by: string;
  old_data: any;
  new_data: any;
  created_at: string;
  updated_at: string;
}

const AuditLogsDashboard = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  
  // Listing & Table state
  const [loading, setLoading] = useState(false);
  const [auditLogsList, setAuditLogsList] = useState<AuditLog[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [searchText, setSearchText] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [tableNameFilter, setTableNameFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [sortedInfo, setSortedInfo] = useState<SorterResult<AuditLog>>({});

  // Modal display states
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  // Fetch pending refund requests
  const fetchAuditLogs = () => {
    setLoading(true);
    InstitutionUserService.getAuditLogs(
      pagination.pageSize,
      pagination.current,
      searchText,
      actionFilter,
      tableNameFilter,
      fromDate,
      toDate
    )
      .then((res: any) => {
        if (res.status === 200 || res?.data) {
          const logsData = res?.data || res;
          const metaData = res?.meta || {};
          setAuditLogsList(logsData || []);
          setPagination(prev => ({
            ...prev,
            total: metaData?.total || logsData?.length || 0
          }));
        } else {
          setAuditLogsList([]);
          setPagination(prev => ({ ...prev, total: 0 }));
        }
        setLoading(false);
      })
      .catch((err: any) => {
        setLoading(false);
        message.error(err?.message || 'Failed to fetch system audit logs');
      });
  };

  useEffect(() => {
    fetchAuditLogs();
    // eslint-disable-next-line
  }, [pagination.current, pagination.pageSize, actionFilter, tableNameFilter, fromDate, toDate]);

  // Debounced search trigger
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchAuditLogs();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line
  }, [searchText]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleTableChange = (newPagination: any, _filters: any, sorter: any) => {
    setPagination(prev => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize
    }));
    setSortedInfo(sorter as SorterResult<AuditLog>);
  };

  const clearFilters = () => {
    setSearchText('');
    setActionFilter('');
    setTableNameFilter('');
    setFromDate('');
    setToDate('');
    form.resetFields();
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const formatDate = (dateString: any) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  };

  const renderJsonData = (title: string, data: any) => {
    if (!data) return <div className="text-gray-400 font-medium text-xs p-2 bg-gray-50 rounded">No data</div>;
    let parsedData = data;
    if (typeof data === 'string') {
      try {
        parsedData = JSON.parse(data);
      } catch {
        parsedData = data;
      }
    }
    return (
      <div className="p-3 bg-slate-900 text-slate-100 rounded-lg overflow-x-auto border border-slate-800 shadow-inner max-h-60 overflow-y-auto">
        <span className="text-gray-400 font-semibold text-xs uppercase block mb-1">{title}</span>
        <pre className="text-xs font-mono m-0" style={{ fontFamily: 'monospace' }}>
          {typeof parsedData === 'object' ? JSON.stringify(parsedData, null, 2) : parsedData}
        </pre>
      </div>
    );
  };

  // Table Columns
  const columns: ColumnsType<AuditLog> = [
    {
      title: 'No.',
      dataIndex: 'index',
      key: 'index',
      width: 50,
      render: (_text, _record, idx) => {
        const pageNumber = pagination.current || 1;
        const pageSize = pagination.pageSize || 10;
        const globalIndex = (pageNumber - 1) * pageSize + (idx + 1);
        return <span>{globalIndex}</span>;
      }
    },
    {
      title: 'Logged Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (val) => formatDate(val),
      sorter: (a, b) => new Date(a?.created_at || '').getTime() - new Date(b?.created_at || '').getTime(),
      sortOrder: sortedInfo.columnKey === 'created_at' ? sortedInfo.order : null,
      width: 170
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      render: (text) => {
        let color = 'default';
        if (text?.includes('FAILED') || text?.includes('ERROR')) {
          color = 'error';
        } else if (
          text?.includes('APPROVE') ||
          text?.includes('WON') ||
          text?.includes('INITIATE') ||
          text?.includes('CREATE_SYSTEM_SETTING') ||
          text?.includes('SEED_SYSTEM_SETTING')
        ) {
          color = 'success';
        } else if (text?.includes('UPDATE') || text?.includes('EDIT') || text?.includes('UPDATE_SYSTEM_SETTING')) {
          color = 'processing';
        } else if (text?.includes('DELETE_SYSTEM_SETTING')) {
          color = 'error';
        }
        return (
          <Tag color={color} style={{ borderRadius: 4, fontWeight: 600 }}>
            {text}
          </Tag>
        );
      },
      sorter: (a, b) => (a?.action || '').localeCompare(b?.action || ''),
      sortOrder: sortedInfo.columnKey === 'action' ? sortedInfo.order : null
    },
    {
      title: 'Done By (Role)',
      dataIndex: 'created_by',
      key: 'created_by',
      render: (text) => (
        <span className="font-semibold text-gray-700">
          {text || 'System'}
        </span>
      ),
      sorter: (a, b) => (a?.created_by || '').localeCompare(b?.created_by || ''),
      sortOrder: sortedInfo.columnKey === 'created_by' ? sortedInfo.order : null
    },
    {
      title: 'Target Table',
      dataIndex: 'table_name',
      key: 'table_name',
      render: (text) => (
        <code className="px-2 py-0.5 bg-gray-150 rounded text-xs text-gray-600 font-mono">
          {text || '-'}
        </code>
      ),
      sorter: (a, b) => (a?.table_name || '').localeCompare(b?.table_name || ''),
      sortOrder: sortedInfo.columnKey === 'table_name' ? sortedInfo.order : null
    },
    {
      title: 'Record ID',
      dataIndex: 'record_id',
      key: 'record_id',
      ellipsis: true
    },
    {
      title: 'Details',
      key: 'action',
      width: 80,
      align: 'center',
      render: (_text, record) => (
        <Button
          type="link"
          icon={<EyeOutlined style={{ fontSize: '18px', color: '#37762f' }} />}
          onClick={() => {
            setSelectedLog(record);
            setDetailModalVisible(true);
          }}
        />
      )
    }
  ];

  return (
    <div className="w-full p-6 bg-gray-50">
      {/* Custom Styles */}
      <style>{`
        .enhanced-card {
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
          transition: all 0.2s ease;
        }
        .section-header {
          background: rgba(55, 118, 47, 0.08);
          color: #111827;
          padding: 12px 16px;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 16px;
        }
        .ant-table-thead > tr > th {
          background: linear-gradient(135deg, rgba(55, 118, 47, 0.1) 0%, rgba(74, 124, 89, 0.06) 100%) !important;
          border-bottom: 2px solid #cbd5e1 !important;
          color: #374151 !important;
          font-weight: 600 !important;
        }
        .ant-table-tbody > tr > td {
          border-bottom: 1px solid #e5e7eb !important;
        }
        .ant-table-tbody > tr:hover > td {
          background: rgba(55, 118, 47, 0.04) !important;
        }
      `}</style>

      {/* Page Header */}
      <div className="px-5 py-4 border-b border-gray-300" style={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        borderRadius: '0 0 12px 12px',
        marginBottom: '20px',
      }}>
        <div className="flex justify-between items-center">
          <div className="text-title font-semibold text-gray-800 flex items-center">
            <div style={{
              background: 'linear-gradient(135deg, #37762f 0%, #4a7c59 100%)',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '12px',
              boxShadow: '0 4px 8px rgba(55, 118, 47, 0.3)',
            }}>
              <span style={{ fontSize: '18px', color: 'white' }}><HistoryOutlined /></span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800 m-0">System Audit Logs Dashboard</h2>
              <p className="text-gray-500 m-0 text-sm font-medium">Auditing portal to track user activities, transfer events, and data modifications</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <Card className="mb-6 rounded-xl border border-gray-200 shadow-sm">
        <Form form={form} layout="vertical">
          <Row gutter={[16, 16]} align="bottom">
            <Col xs={24} md={8} xl={6}>
              <Form.Item name="search" label={<span className="font-semibold text-gray-700">Quick Search</span>} style={{ marginBottom: 0 }}>
                <Input
                  placeholder="Search record ID, user, table..."
                  prefix={<ZoomOutOutlined style={{ color: '#6b7280' }} />}
                  onChange={handleSearch}
                  value={searchText}
                  style={{ borderRadius: '8px', height: '40px' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} xl={5}>
              <Form.Item name="action" label={<span className="font-semibold text-gray-700">Action Type</span>} style={{ marginBottom: 0 }}>
                <Select
                  showSearch
                  placeholder="Filter by Action"
                  style={{ borderRadius: '8px', width: '100%', height: '40px' }}
                  onChange={(val) => { setActionFilter(val || ''); setPagination(prev => ({ ...prev, current: 1 })); }}
                  allowClear
                >
                  <Option value="APPROVE_AND_INITIATE">APPROVE_AND_INITIATE</Option>
                  <Option value="REFUND_BK_AUTHENTICATION_FAILED">REFUND_BK_AUTHENTUATION_FAILED</Option>
                  <Option value="REFUND_BK_INITIATION_FAILED">REFUND_BK_INITIATION_FAILED</Option>
                  <Option value="REFUND_BK_INITIATION_ERROR">REFUND_BK_INITIATION_ERROR</Option>
                  <Option value="WON">WON</Option>
                  <Option value="LOST">LOST</Option>
                  <Option value="CREATE">CREATE</Option>
                  <Option value="UPDATE">UPDATE</Option>
                  <Option value="DELETE">DELETE</Option>
                  <Option value="CREATE_SYSTEM_SETTING">CREATE_SYSTEM_SETTING</Option>
                  <Option value="UPDATE_SYSTEM_SETTING">UPDATE_SYSTEM_SETTING</Option>
                  <Option value="DELETE_SYSTEM_SETTING">DELETE_SYSTEM_SETTING</Option>
                  <Option value="SEED_SYSTEM_SETTING">SEED_SYSTEM_SETTING</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} xl={5}>
              <Form.Item name="table_name" label={<span className="font-semibold text-gray-700">Target Table</span>} style={{ marginBottom: 0 }}>
                <Select
                  showSearch
                  placeholder="Filter by Table"
                  style={{ borderRadius: '8px', width: '100%', height: '40px' }}
                  onChange={(val) => { setTableNameFilter(val || ''); setPagination(prev => ({ ...prev, current: 1 })); }}
                  allowClear
                >
                  <Option value="refund_request">refund_request (Bidders)</Option>
                  <Option value="bailiff_refund_request">bailiff_refund_request (Bailiffs)</Option>
                  <Option value="bidders_bids">bidders_bids</Option>
                  <Option value="property">property</Option>
                  <Option value="institution">institution</Option>
                  <Option value="users">users</Option>
                  <Option value="system_settings">system_settings (Settings)</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={5}>
              <Form.Item name="range_picker" label={<span className="font-semibold text-gray-700">Logged Date Range</span>} style={{ marginBottom: 0 }}>
                <RangePicker
                  className="w-full"
                  allowClear
                  style={{ borderRadius: '8px', height: '40px' }}
                  onChange={(dates) => {
                    if (dates) {
                      setFromDate(dates[0] ? dates[0].startOf('day').toISOString() : '');
                      setToDate(dates[1] ? dates[1].endOf('day').toISOString() : '');
                    } else {
                      setFromDate('');
                      setToDate('');
                    }
                    setPagination(prev => ({ ...prev, current: 1 }));
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} xl={3}>
              <Button
                onClick={clearFilters}
                className="w-full"
                style={{ borderRadius: '8px', height: '40px', fontWeight: 600 }}
              >
                Clear
              </Button>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* Logs Table */}
      <Card className="rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <Spin spinning={loading} indicator={antIcon} tip="Loading system audit logs...">
          <Table
            rowKey="audit_logs_id"
            columns={columns}
            dataSource={auditLogsList}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} audit logs`
            }}
            onChange={handleTableChange}
            scroll={{ x: 800 }}
            rowClassName={(record, index) => (index % 2 === 0 ? 'bg-white' : 'bg-gray-50')}
          />
        </Spin>
      </Card>

      {/* Audit Detail View Modal */}
      <Modal
        title={
          <div style={{
            background: 'linear-gradient(135deg, #37762f 0%, #4a7c59 100%)',
            color: 'white',
            padding: '16px 20px',
            borderRadius: '12px 12px 0 0',
            fontSize: '18px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            margin: '-24px -24px 0 -24px',
          }}>
            <span>📄</span>
            System Audit Log Record Details
          </div>
        }
        visible={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)} style={{ borderRadius: '8px', fontWeight: 600 }}>
            Close
          </Button>
        ]}
        width="65%"
        centered
        destroyOnClose
      >
        {selectedLog && (
          <div className="py-4">
            {/* Header metadata */}
            <div className="mb-6 p-4 rounded-xl border border-gray-200 bg-gray-50 shadow-sm">
              <div className="section-header">
                <span>🔍</span>
                Audit Metadata
              </div>
              <Row gutter={[16, 16]}>
                <Col xs={24} md={8}>
                  <div className="font-semibold text-gray-500 text-xs">Action Type</div>
                  <div className="text-sm font-semibold text-gray-800">{selectedLog.action || '-'}</div>
                </Col>
                <Col xs={24} md={8}>
                  <div className="font-semibold text-gray-500 text-xs">Executed By (Role)</div>
                  <div className="text-sm font-semibold text-gray-800">{selectedLog.created_by || 'System'}</div>
                </Col>
                <Col xs={24} md={8}>
                  <div className="font-semibold text-gray-500 text-xs">Done By (User ID)</div>
                  <div className="text-sm font-semibold text-gray-800">{selectedLog.user_id || '-'}</div>
                </Col>
              </Row>
              <Row gutter={[16, 16]} className="pt-4">
                <Col xs={24} md={8}>
                  <div className="font-semibold text-gray-500 text-xs">Target Table Name</div>
                  <div className="text-sm font-semibold text-gray-800">{selectedLog.table_name || '-'}</div>
                </Col>
                <Col xs={24} md={8}>
                  <div className="font-semibold text-gray-500 text-xs">Record Reference ID</div>
                  <div className="text-sm font-semibold text-gray-800">{selectedLog.record_id || '-'}</div>
                </Col>
                <Col xs={24} md={8}>
                  <div className="font-semibold text-gray-500 text-xs">Logged Timestamp</div>
                  <div className="text-sm font-semibold text-gray-800">{formatDate(selectedLog.created_at)}</div>
                </Col>
              </Row>
            </div>

            {/* Changes details */}
            <div className="p-4 rounded-xl border border-gray-200 bg-gray-50 shadow-sm">
              <div className="section-header">
                <span>🔄</span>
                Data Modifications (Payload Diff)
              </div>
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <div className="font-semibold text-gray-600 text-xs mb-1">Old Data Values</div>
                  {renderJsonData("Old Object State", selectedLog.old_data)}
                </Col>
                <Col xs={24} md={12}>
                  <div className="font-semibold text-gray-600 text-xs mb-1">New Data Values</div>
                  {renderJsonData("New Object State", selectedLog.new_data)}
                </Col>
              </Row>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AuditLogsDashboard;
